/*
 * Turns a set of selected template-slide ids into slides in the deck. Shared
 * by Slide Templates (slide-templates/services/slideTemplateInsert.ts) and
 * Presentation Templates (presentation-templates/services/
 * presentationTemplateInsert.ts) — both features fetch each entry's own
 * source `.pptx` (a directory-SAS link, possibly holding several other
 * templates' slides too) and carve out just the slides the user picked, with
 * no insertable payload ever living in either feature's own manifest.
 *
 * MUST only ever run in the task pane: PowerPoint.run() is unavailable inside
 * an Office Dialog window (insertSlideFromBase64 throws there by design).
 *
 * ONE FETCH + ONE INSERT PER SOURCE FILE, NOT PER SLIDE
 * A naive per-slide loop (fetch the source, carve out one slide, insert,
 * repeat) re-downloads the SAME source `.pptx` once per slide and pays a full
 * PowerPoint.run() round trip per slide too — selecting many entries from the
 * same source file would mean that many downloads and that many separate
 * Office.js calls. This instead groups the selection into runs of consecutive
 * same-source entries (preserving overall order), fetches each source
 * exactly once, and hands `buildSubDeckBase64` the WHOLE run's slide
 * positions at once — `insertSlidesFromBase64` (see
 * create-slide/hooks/slideInsert.ts) already natively inserts every slide in
 * a base64 package in a single call, so this only needed the package to
 * contain more than one slide to begin with.
 */

import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { insertSlideFromBase64, insertSlideFromBase64At, type SlideInsertPosition } from "../../create-slide/hooks/slideInsert";
import { sasVersionHeaders } from "./blobImageUpload";
import { HttpStatusError } from "../../../services/deckorateApi";
import { getErrorMessage } from "./connectorErrors";

/** The fields `insertTemplateSlides` needs from a caller's own template/slide
 *  type — Slide Templates' `SlideTemplate` and Presentation Templates'
 *  `PresentationTemplateSlide` both satisfy this shape already. */
export interface InsertableTemplateSlide {
  readonly id: string;
  readonly insertable: boolean;
  readonly sourceFileUrl: string | null;
  readonly slideIndex: number | null;
}

export interface TemplateSlideInsertMessages {
  /** Thrown when nothing in the selection is insertable. */
  readonly noInsertableSelection: string;
  /** Prefixed to a source-file download failure's status/body. */
  readonly sourceDownloadFailed: string;
}

/**
 * Optional extras a caller can layer on top of the fetch/carve/insert
 * pipeline. `patchSlideXml` and `sourceCache` were added for Create Slides'
 * template path, which needs the SAME proven fetch + slide-carve + insert as
 * the two template browsers but has to rewrite the slide's text on the way
 * through.
 */
export interface TemplateSlideInsertOptions<T = InsertableTemplateSlide> {
  /**
   * Rewrites one selected slide's XML before the package is rebuilt. Called
   * with the slide part's raw XML and the entry it belongs to; return the
   * replacement. Omit it and slides are inserted byte-for-byte, which is what
   * the template browsers do.
   */
  readonly patchSlideXml?: (slideXml: string, template: T) => string;
  /**
   * Reused across calls so one source `.pptx` downloads once even when the
   * caller inserts a slide at a time (Create Slides does, to keep per-slide
   * progress and per-slide error isolation).
   */
  readonly sourceCache?: Map<string, Promise<ArrayBuffer>>;
  /** Insert after the slide currently selected in the deck instead of at the end. */
  readonly insertAfterCurrent?: boolean;
  /** Replaces the GET of the source file, for sources that may live under several filenames. */
  readonly fetchSource?: (sourceFileUrl: string) => Promise<ArrayBuffer>;
}

export interface TemplateSlideInsertTelemetry {
  readonly log: (reason: string, message: string) => void;
  /** Feature-specific prefix for this call's own telemetry reasons, e.g.
   *  "slide-templates" or "presentation-templates". */
  readonly reasonPrefix: string;
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000; // stay under the arg-count limit of String.fromCodePoint
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

const RELATIONSHIPS_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const R_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const P_NS = "http://schemas.openxmlformats.org/presentationml/2006/main";

/**
 * Zip-entry path of the slide at `position` (0-based, presentation order):
 * `<p:sldIdLst>`'s Nth `<p:sldId r:id>` resolved through
 * `ppt/_rels/presentation.xml.rels`.
 *
 * Deliberately NOT `ppt/slides/slide{N+1}.xml` — a template's part names
 * don't have to match presentation order, and guessing would patch the wrong
 * slide's text while still producing a valid package.
 */
function resolveSlidePartPath(files: Record<string, Uint8Array>, position: number): string {
  const presDoc = new DOMParser().parseFromString(strFromU8(files["ppt/presentation.xml"]!), "application/xml");
  const sldIds = Array.from(presDoc.getElementsByTagNameNS(P_NS, "sldId"));
  const target = sldIds[position];
  if (!target) throw new Error(`Original slide ${position + 1} was not found.`);
  const relId = target.getAttributeNS(R_NS, "id");
  if (!relId) throw new Error(`Original slide ${position + 1} carries no relationship id.`);

  const relsBytes = files["ppt/_rels/presentation.xml.rels"];
  if (!relsBytes) throw new Error("The original deck is missing ppt/_rels/presentation.xml.rels.");
  const relsDoc = new DOMParser().parseFromString(strFromU8(relsBytes), "application/xml");
  for (const rel of Array.from(relsDoc.getElementsByTagNameNS(RELATIONSHIPS_NS, "Relationship"))) {
    if (rel.getAttribute("Id") !== relId) continue;
    const partTarget = rel.getAttribute("Target") ?? "";
    if (!partTarget) break;
    // Targets are relative to ppt/ ("slides/slide3.xml"), sometimes absolute
    // ("/ppt/slides/slide3.xml").
    return partTarget.startsWith("/") ? partTarget.slice(1) : `ppt/${partTarget.replace(/^\.\//, "")}`;
  }
  throw new Error(`Could not resolve original slide ${position + 1} to a package part.`);
}

/**
 * Build a `.pptx` (zip bytes) containing the slides at `slidePositions`
 * (0-based, in presentation order), in the given order, from `originalDeck`.
 * Also what a "download selected slides" pack is made from.
 *
 * `patch`, when given, rewrites each selected slide's own XML first, keyed by
 * the same position. Every other part is left exactly as it was, which is
 * what keeps the template's layout, master, theme and media intact.
 */
export function buildSubDeckBytes(
  originalDeck: ArrayBuffer,
  slidePositions: readonly number[],
  patch?: (slideXml: string, position: number) => string,
): Uint8Array {
  if (slidePositions.length === 0) throw new Error("No slide positions were given to build a sub-deck from.");

  const files = unzipSync(new Uint8Array(originalDeck));
  const presBytes = files["ppt/presentation.xml"];
  if (!presBytes) throw new Error("The original deck is missing ppt/presentation.xml.");

  if (patch) {
    for (const position of slidePositions) {
      const partPath = resolveSlidePartPath(files, position);
      const slideBytes = files[partPath];
      if (!slideBytes) throw new Error(`The original deck is missing ${partPath}.`);
      files[partPath] = strToU8(patch(strFromU8(slideBytes), position));
    }
  }

  const xml = strFromU8(presBytes);
  const listBody = /<p:sldIdLst>([\s\S]*?)<\/p:sldIdLst>/.exec(xml)?.[1];
  if (listBody == null) throw new Error("The original deck has no slide list.");

  const sldIds = listBody.match(/<p:sldId\b[^>]*?\/>/g) ?? [];
  const selected = slidePositions.map((position) => {
    const target = sldIds[position];
    if (!target) throw new Error(`Original slide ${position + 1} was not found.`);
    return target;
  });

  const trimmed = xml.replace(/<p:sldIdLst>[\s\S]*?<\/p:sldIdLst>/, `<p:sldIdLst>${selected.join("")}</p:sldIdLst>`);
  files["ppt/presentation.xml"] = strToU8(trimmed);
  return zipSync(files);
}

/** Same sub-deck as `buildSubDeckBytes`, base64-encoded for `insertSlidesFromBase64`. */
function buildSubDeckBase64(
  originalDeck: ArrayBuffer,
  slidePositions: readonly number[],
  patch?: (slideXml: string, position: number) => string,
): string {
  return uint8ToBase64(buildSubDeckBytes(originalDeck, slidePositions, patch));
}

/** One consecutive run of selected entries that all share a `sourceFileUrl`. */
interface SourceRun<T> {
  readonly sourceFileUrl: string;
  readonly templates: T[];
}

/**
 * Groups `insertable` into runs of consecutive entries sharing one
 * `sourceFileUrl`, preserving overall order.
 *
 * Assumes every entry already passed `isFullyInsertable` (non-null
 * `sourceFileUrl`/`slideIndex`); callers filter the selection before this runs.
 */
function groupBySourceFile<T extends InsertableTemplateSlide>(insertable: readonly T[]): SourceRun<T>[] {
  const runs: SourceRun<T>[] = [];
  for (const template of insertable) {
    const sourceFileUrl = template.sourceFileUrl!;
    const last = runs[runs.length - 1];
    if (last?.sourceFileUrl === sourceFileUrl) {
      last.templates.push(template);
    } else {
      runs.push({ sourceFileUrl, templates: [template] });
    }
  }
  return runs;
}

async function downloadSourceBuffer(
  sourceFileUrl: string,
  messages: TemplateSlideInsertMessages,
  telemetry: TemplateSlideInsertTelemetry,
): Promise<ArrayBuffer> {
  const res = await fetch(sourceFileUrl, {
    method: "GET",
    // Same user-delegation-SAS requirement as the manifest download — see
    // shared/services/blobImageUpload.ts's SAS_VERSION_HEADER doc.
    headers: sasVersionHeaders(sourceFileUrl, telemetry.log, `${telemetry.reasonPrefix}-source-sas-version-parse`),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      telemetry.log(`${telemetry.reasonPrefix}-source-body-read`, getErrorMessage(error, "Failed to read source-file error body"));
      return "";
    });
    throw new HttpStatusError(res.status, `${messages.sourceDownloadFailed} (${res.status} ${errText})`);
  }
  return res.arrayBuffer();
}

/** Downloads a source file, or reuses an in-flight/completed download of it.
 *  Exported so a caller can seed `sourceCache` ahead of the sequential
 *  inserts (create-slide's templateAssembler.ts prefetchTemplateSources), or
 *  just download one source file (presentationTemplateInsert.ts's packs). */
export function fetchSourceBuffer(
  sourceFileUrl: string,
  messages: TemplateSlideInsertMessages,
  telemetry: TemplateSlideInsertTelemetry,
  sourceCache?: Map<string, Promise<ArrayBuffer>>,
): Promise<ArrayBuffer> {
  const cached = sourceCache?.get(sourceFileUrl);
  if (cached) return cached;
  const pending = downloadSourceBuffer(sourceFileUrl, messages, telemetry);
  sourceCache?.set(sourceFileUrl, pending);
  return pending;
}

/**
 * `insertable` (see each feature's own manifest type) is trusted for the
 * normal manifest path, but re-checked here for real — a stale/tampered
 * selection must not reach `groupBySourceFile`'s non-null assertions with a
 * null field.
 */
function isFullyInsertable(template: InsertableTemplateSlide): boolean {
  return template.insertable && template.sourceFileUrl !== null && template.slideIndex !== null;
}

/**
 * Inserts the selected templates, in the order `templates` lists them.
 *
 * Order comes from `templates`, not from `selectedIds`, so the deck matches
 * what the grid showed rather than the order the user happened to click. Runs
 * are inserted sequentially (concurrent insertSlidesFromBase64 calls would
 * race for the same insertion point), but each run is one batched call
 * covering every entry in it — see `groupBySourceFile`.
 *
 * Throws when nothing could be inserted (so the caller can show one clear
 * error) and propagates the first run's failure otherwise, leaving any
 * already-inserted runs in place — a partial insert is recoverable by the
 * user, a silent no-op is not.
 *
 * @returns how many slides were inserted.
 */
export async function insertTemplateSlides<T extends InsertableTemplateSlide>(
  templates: readonly T[],
  selectedIds: readonly string[],
  messages: TemplateSlideInsertMessages,
  telemetry: TemplateSlideInsertTelemetry,
  options: TemplateSlideInsertOptions<T> = {},
): Promise<number> {
  const wanted = new Set(selectedIds);
  const insertable = templates.filter((template) => wanted.has(template.id) && isFullyInsertable(template));
  if (insertable.length === 0) throw new Error(messages.noInsertableSelection);

  const { patchSlideXml, sourceCache, fetchSource } = options;
  const runs = groupBySourceFile(insertable);
  // Every source file starts downloading now; inserting stays strictly in
  // order (a promise chain) so the deck order is deterministic.
  const buffers = runs.map(
    (run) => fetchSource?.(run.sourceFileUrl) ?? fetchSourceBuffer(run.sourceFileUrl, messages, telemetry, sourceCache),
  );
  // Awaited in turn below; this only stops an "unhandled rejection" for a
  // file whose run is never reached because an earlier one failed.
  for (const pending of buffers) pending.catch(() => undefined);
  let inserted = 0;
  // With `insertAfterCurrent`, the first batch goes right after the slide the
  // user is on and every later batch chains after the last slide the previous
  // one added, so the deck ends up in the same order as the selection.
  // Otherwise everything is appended to the end of the deck.
  let position: SlideInsertPosition = options.insertAfterCurrent ? "current" : "end";
  await runs.reduce(async (previous, run, index) => {
    await previous;
    const buffer = await buffers[index]!;
    // slideIndex is 1-based (presentation order); buildSubDeckBase64 wants
    // 0-based positions into the same order.
    const positions = run.templates.map((template) => template.slideIndex! - 1);
    const byPosition = new Map(run.templates.map((template) => [template.slideIndex! - 1, template]));
    const base64 = buildSubDeckBase64(
      buffer,
      positions,
      patchSlideXml ? (slideXml, slidePosition) => patchSlideXml(slideXml, byPosition.get(slidePosition)!) : undefined,
    );
    if (position === "end") {
      await insertSlideFromBase64(base64); // NOSONAR S9382 - insertion order matters
    } else {
      const lastInsertedId = await insertSlideFromBase64At(base64, position); // NOSONAR S9382 - each run chains after the previous one
      if (lastInsertedId) position = { slideId: lastInsertedId };
    }
    inserted += run.templates.length;
  }, Promise.resolve());
  return inserted;
}

/* ── Insert failures (BRD §10) ───────────────────────────────────────
 * The window closes once an insert answers, so three failures get a pane
 * notice instead: read-only deck, lost connection, selection removed by an
 * admin. Wording lives with each feature; this only decides WHICH one an
 * error is. */

export type InsertFailureKind = "readonly" | "connection" | "removed";

export interface InsertNoticeCopy {
  readonly title: string;
  readonly message: string;
}

/** Thrown when a selected entry is no longer in the organisation's library. */
export class SelectionUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SelectionUnavailableError";
  }
}

/** Thrown before an insert is attempted on a read-only presentation. */
export class DeckReadOnlyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DeckReadOnlyError";
  }
}

/** True when PowerPoint reports the deck as read-only; no host or an unreadable mode counts as editable. */
export function isDeckReadOnly(log: (reason: string, message: string) => void, reason: string): boolean {
  if (typeof Office === "undefined") return false;
  try {
    return Office.context?.document?.mode === Office.DocumentMode.ReadOnly;
  } catch (error) {
    log(reason, getErrorMessage(error, "Couldn't read the document mode"));
    return false;
  }
}

const READ_ONLY_PATTERN = /read[- ]?only|can'?t be edited|cannot be edited|protected view|document is locked/i;
// "load failed" is Safari's bare fetch rejection; anchored so it can't match inside "download failed".
const CONNECTION_PATTERNS: readonly RegExp[] = [
  /failed to fetch|network ?error|network request failed|offline|err_internet/i,
  /timed? ?out|connection (was )?(lost|reset|closed)/i,
  /(?:^|[^a-z])load failed/i,
];

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : "";
}

/** Sorts an insert failure into a BRD notice, or `null` for any other failure. */
export function classifyInsertFailure(error: unknown): InsertFailureKind | null {
  if (error instanceof SelectionUnavailableError) return "removed";
  if (error instanceof DeckReadOnlyError) return "readonly";

  const message = messageOf(error);
  if (READ_ONLY_PATTERN.test(message)) return "readonly";

  // A bare fetch TypeError means unreachable; an HTTP status is a server problem, not "connection lost".
  const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
  if (isOffline || CONNECTION_PATTERNS.some((pattern) => pattern.test(message))) return "connection";

  return null;
}
